import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, X, Pencil, Trash2, AlertTriangle, RefreshCw } from 'lucide-react';

const STATUS_COLOR = {
  active:          'bg-emerald-50 text-emerald-700 border-emerald-200',
  expiring_soon:   'bg-amber-50 text-amber-700 border-amber-200',
  expired:         'bg-red-50 text-red-700 border-red-200',
  cancelled:       'bg-slate-100 text-slate-500 border-slate-200',
};

const blank = () => ({
  title: '', category: '', vendor_name: '', contract_no: '',
  start_date: '', end_date: '', annual_value: '',
  contact_person: '', contact_phone: '', scope: '',
  auto_renew: false, notes: '',
});

export default function AdminAMC() {
  const [items, setItems]   = useState([]);
  const [lookup, setLookup] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(blank());
  const [saving, setSaving]     = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const load = () => {
    const q = statusFilter ? `?status=${statusFilter}` : '';
    apiGet(`/admin/amc${q}`).then(r => setItems(r.items || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { apiGet('/admin/lookup').then(setLookup).catch(() => {}); }, []);

  const openNew  = ()  => { setEditing(null); setForm(blank()); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ title: r.title, category: r.category, vendor_name: r.vendor_name,
      contract_no: r.contract_no || '', start_date: r.start_date, end_date: r.end_date,
      annual_value: r.annual_value || '', contact_person: r.contact_person || '',
      contact_phone: r.contact_phone || '', scope: r.scope || '',
      auto_renew: !!r.auto_renew, notes: r.notes || '' });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.vendor_name || !form.end_date) { toast.error('Title, Vendor, End Date are required'); return; }
    setSaving(true);
    // Convert empty string numeric fields to null
    const payload = {
      ...form,
      annual_value: form.annual_value === '' ? null : Number(form.annual_value) || null,
    };
    try {
      if (editing) {
        await apiPut(`/admin/amc/${editing.id}`, payload);
        toast.success('AMC updated');
      } else {
        await apiPost('/admin/amc', { ...payload, start_date: payload.start_date || new Date().toISOString().slice(0,10) });
        toast.success('AMC added');
      }
      setShowForm(false); load();
    } catch { toast.error('Could not save'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this AMC record?')) return;
    try { await apiDel(`/admin/amc/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const cats = lookup.amc_categories || [];
  const expired  = items.filter(i => i.status === 'expired').length;
  const expiring = items.filter(i => i.status === 'expiring_soon').length;

  return (
    <div data-testid="admin-amc" className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-1">Administration</div>
          <h1 className="font-display text-2xl font-extrabold">AMC Register</h1>
          <p className="text-sm text-slate-500 mt-0.5">Annual Maintenance Contracts — track expiry dates and renewals.</p>
        </div>
        <button onClick={openNew} className="btn-primary flex items-center gap-2" data-testid="amc-add-btn">
          <Plus size={15} /> Add AMC
        </button>
      </div>

      {/* Alert banners */}
      {(expiring > 0 || expired > 0) && (
        <div className="flex gap-2 flex-wrap">
          {expired  > 0 && <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-xs text-red-700 font-display font-semibold"><AlertTriangle size={13} /> {expired} AMC(s) EXPIRED — immediate renewal needed</div>}
          {expiring > 0 && <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-700 font-display font-semibold"><RefreshCw size={13} /> {expiring} AMC(s) expiring within 30 days</div>}
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['', 'active', 'expiring_soon', 'expired', 'cancelled'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs font-display font-bold uppercase tracking-wider rounded-sm border ${statusFilter === s ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#1F7A6E]'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Contract</th><th>Category</th><th>Vendor</th>
            <th>End Date</th><th className="text-right">Value (₹)</th>
            <th>Status</th><th>Auto-Renew</th><th></th>
          </tr></thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-8">No AMC records found</td></tr>
            )}
            {items.map(r => (
              <tr key={r.id} data-testid={`amc-row-${r.id}`}>
                <td>
                  <div className="font-display font-semibold">{r.title}</div>
                  {r.contract_no && <div className="text-[10px] text-slate-400">{r.contract_no}</div>}
                </td>
                <td className="text-xs">{r.category}</td>
                <td className="text-xs">{r.vendor_name}</td>
                <td className="text-xs font-mono">{r.end_date}</td>
                <td className="text-right tabular-nums text-sm">{r.annual_value ? `₹${Number(r.annual_value).toLocaleString()}` : '—'}</td>
                <td><span className={`tag border text-[10px] ${STATUS_COLOR[r.status] || ''}`}>{r.status?.replace('_',' ')}</span></td>
                <td className="text-xs text-center">{r.auto_renew ? 'Yes' : 'No'}</td>
                <td>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => openEdit(r)} className="text-slate-400 hover:text-[#1F7A6E]" data-testid={`amc-edit-${r.id}`}><Pencil size={13} /></button>
                    <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-600" data-testid={`amc-del-${r.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-md bg-white h-full shadow-xl flex flex-col" onClick={e => e.stopPropagation()}>
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#1F7A6E] text-white">
              <h2 className="font-display font-bold text-sm uppercase tracking-wider">{editing ? 'Edit AMC' : 'Add AMC'}</h2>
              <button onClick={() => setShowForm(false)}><X size={18} /></button>
            </header>
            <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="label">Contract Title *</label>
                <input className="input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="e.g. AC Annual Service Contract" data-testid="amc-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Category *</label>
                  <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                    <option value="">Select</option>
                    {cats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Contract No.</label>
                  <input className="input" value={form.contract_no} onChange={e => setForm({...form, contract_no: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="label">Vendor Name *</label>
                <input className="input" value={form.vendor_name} onChange={e => setForm({...form, vendor_name: e.target.value})} data-testid="amc-vendor" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Start Date</label>
                  <input type="date" className="input" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} />
                </div>
                <div>
                  <label className="label">End Date *</label>
                  <input type="date" className="input" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="amc-end-date" />
                </div>
              </div>
              <div>
                <label className="label">Annual Value (₹)</label>
                <input type="number" min="0" className="input" value={form.annual_value} onChange={e => setForm({...form, annual_value: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Contact Person</label>
                  <input className="input" value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input className="input" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="label">Scope of Work</label>
                <textarea className="input h-16 resize-none" value={form.scope} onChange={e => setForm({...form, scope: e.target.value})} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="auto_renew" checked={form.auto_renew} onChange={e => setForm({...form, auto_renew: e.target.checked})} className="w-4 h-4 accent-[#1F7A6E]" />
                <label htmlFor="auto_renew" className="text-sm cursor-pointer">Auto-renew before expiry</label>
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input h-16 resize-none" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </form>
            <footer className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="amc-save">{saving ? 'Saving…' : (editing ? 'Update' : 'Add AMC')}</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
